import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container, Typography, Box, Grid, Card, CircularProgress, Alert, Button, Stack
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Forum as ForumIcon, Add, ChevronRight } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import forumService from '../../services/forumService';
import ThreadListItem from '../../components/Forum/ThreadListItem';

const SectionHeading = ({ overline, title, subtitle, id, action }) => {
  const theme = useTheme();
  return (
    <Box id={id} sx={{ mb: 3, scrollMarginTop: 90 }}>
      <Typography variant="overline" sx={{
        color: 'primary.light',
        letterSpacing: '0.12em',
        fontSize: '0.72rem',
        fontWeight: 700,
        display: 'block',
        mb: 0.5,
      }}>
        {overline}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h2" sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          color: 'primary.dark',
          lineHeight: 1.2,
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}>
          {title}
        </Typography>
        <Box sx={{
          flex: 1,
          minWidth: 40,
          maxWidth: 120,
          height: 2,
          mt: 0.5,
          borderRadius: 1,
          background: `linear-gradient(to right, ${theme.palette.secondary.main}, transparent)`,
          display: { xs: 'none', sm: 'block' },
        }} />
        {action}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const EmptyBox = ({ children }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      py: 6,
      px: 2,
      textAlign: 'center',
      bgcolor: 'background.paper',
      borderRadius: '14px',
      border: `1px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
    }}>
      {children}
    </Box>
  );
};

const ForumHome = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [recentThreads, setRecentThreads] = useState([]);
  const [activeThreads, setActiveThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const accentColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.primary.light,
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, threadRes, activeRes] = await Promise.all([
          forumService.getCategories(),
          forumService.getThreads({ limit: 8, sort: 'new' }),
          forumService.getThreads({ limit: 6, sort: 'active' })
        ]);
        if (catRes.success) setCategories(catRes.data.categories);
        if (threadRes.success) setRecentThreads(threadRes.data.threads);
        if (activeRes.success) setActiveThreads(activeRes.data.threads);
      } catch {
        setError('Nem sikerült betölteni a fórumot.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
      <CircularProgress color="primary" size={36} />
      <Typography variant="body2" color="text.secondary">Fórum betöltése...</Typography>
    </Box>
  );

  if (error) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Újrapróbálom</Button>}>
        {error}
      </Alert>
    </Container>
  );

  // Csak a ténylegesen hozzászólást kapott témák számítanak "aktivitásnak"
  const activityThreads = activeThreads.filter((t) => !!t.lastReplyAt);

  return (
    <>
      <Helmet>
        <title>Esküvői Fórum – Wedigo</title>
        <meta name="description" content="Esküvőtervezők közössége – tippek, ajánlások, tapasztalatok" />
      </Helmet>

      {/* Hero – kompaktabb, hogy az aktivitás hamarabb látszódjon */}
      <Box sx={{
        background: `linear-gradient(150deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, #3a7042 100%)`,
        color: '#fff',
        py: { xs: 5, md: 7 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle, ${alpha('#fff', 0.06)} 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -80,
          right: -80,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '50%',
            border: `2px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
            mb: 2,
            background: alpha(theme.palette.secondary.main, 0.12),
          }}>
            <ForumIcon sx={{ fontSize: 30, color: theme.palette.secondary.main }} />
          </Box>

          <Typography variant="h2" component="h1" sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            fontSize: { xs: '1.9rem', md: '2.5rem' },
            letterSpacing: '-0.02em',
            mb: 1.5,
            lineHeight: 1.2,
          }}>
            Esküvői Fórum
          </Typography>

          <Typography variant="body1" sx={{
            opacity: 0.88,
            maxWidth: 520,
            mx: 'auto',
            mb: 3,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            lineHeight: 1.7,
          }}>
            Valódi tapasztalatok, őszinte vélemények — az algoritmus nélkül.
            Kérdezz, osszd meg élményedet, tanulj másoktól!
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="stretch" sx={{ maxWidth: { xs: 360, sm: 'none' }, mx: 'auto' }}>
            <Button
              component={Link}
              to="/forum/uj-tema"
              variant="contained"
              startIcon={<Add />}
              size="large"
              sx={{
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.primary.dark,
                fontWeight: 700,
                px: 4,
                minHeight: 48,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: theme.palette.secondary.light,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.45)}`,
                },
              }}
            >
              Új téma indítása
            </Button>
            <Button
              href="#kategoriak"
              variant="outlined"
              size="large"
              sx={{
                borderColor: alpha('#fff', 0.55),
                color: '#fff',
                px: 3.5,
                minHeight: 48,
                borderRadius: '10px',
                '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) },
              }}
            >
              Böngészd a témaköröket
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>

        {/* 1) Friss aktivitás – legfelül, ez a fórum "pulzusa" */}
        <SectionHeading
          overline="AKTIVITÁS"
          title="Friss aktivitás"
          subtitle="Legutóbb hozzászólást kapott témák"
        />
        {activityThreads.length === 0 ? (
          <EmptyBox>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Még nincs hozzászólás egyetlen témában sem.
            </Typography>
            <Button component={Link} to="/forum/uj-tema" variant="outlined" startIcon={<Add />}>
              Indíts beszélgetést
            </Button>
          </EmptyBox>
        ) : (
          <Stack spacing={1.25}>
            {activityThreads.map((thread) => (
              <ThreadListItem key={thread.id} thread={thread} activity />
            ))}
          </Stack>
        )}

        <Box sx={{ mb: { xs: 5, md: 7 } }} />

        {/* 2) Témakörök */}
        <SectionHeading id="kategoriak" overline="FÓRUM" title="Témakörök" subtitle="Válaszd ki, miről szeretnél beszélgetni" />
        <Grid container spacing={{ xs: 1.5, md: 2.5 }}>
          {categories.map((cat, index) => {
            const accent = accentColors[index % accentColors.length];
            const count = cat._count?.threads ?? 0;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat.id}>
                <Card
                  component={Link}
                  to={`/forum/${cat.slug}`}
                  sx={{
                    textDecoration: 'none',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: { xs: 1.75, md: 2.25 },
                    minHeight: 96,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    borderLeft: `4px solid ${accent}`,
                    bgcolor: 'background.paper',
                    borderRadius: '14px',
                    boxShadow: `0 2px 10px ${alpha(theme.palette.primary.dark, 0.05)}`,
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      transform: { md: 'translateY(-3px)' },
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.16)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{
                    width: 52,
                    height: 52,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '14px',
                    fontSize: 28,
                    bgcolor: alpha(accent, 0.12),
                  }}>
                    {cat.icon}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography component="h3" variant="subtitle1" sx={{
                      fontWeight: 700, color: 'text.primary', lineHeight: 1.3, overflowWrap: 'anywhere',
                    }}>
                      {cat.name}
                    </Typography>
                    {cat.description && (
                      <Typography variant="body2" color="text.secondary" sx={{
                        mt: 0.25,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                      }}>
                        {cat.description}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 600, color: 'primary.main' }}>
                      {count} téma
                    </Typography>
                  </Box>

                  <ChevronRight sx={{ color: 'text.disabled', flexShrink: 0 }} />
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mb: { xs: 5, md: 7 } }} />

        {/* 3) Legfrissebb témák */}
        <SectionHeading overline="LEGFRISSEBB" title="Legújabb témák" subtitle="A most indított beszélgetések" />
        {recentThreads.length === 0 ? (
          <EmptyBox>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Még nincsenek témák. Légy te az első!
            </Typography>
            <Button component={Link} to="/forum/uj-tema" variant="outlined" color="primary" startIcon={<Add />}>
              Új téma indítása
            </Button>
          </EmptyBox>
        ) : (
          <Stack spacing={1.25}>
            {recentThreads.map((thread) => (
              <ThreadListItem key={thread.id} thread={thread} />
            ))}
          </Stack>
        )}
      </Container>
    </>
  );
};

export default ForumHome;
